// Mock data for DriveHR application
export const mockData = {
  // Hero section data
  hero: {
    tagline: "Driving your HR Forward, Together!",
    headline: "Practical HR Solutions for Small & Growing Businesses",
    subheadline: "Get professional HR templates, policies, and guidance designed for busy small business owners. Simple, affordable, and ready to use today.",
    ctaText: "Start Your HR Journey",
    trustSignals: [
      {
        title: "15+ Years HR Experience",
        description: "Hands-on HR support across hiring, compliance, and employee relations."
      },
      {
        title: "12+ Industries Supported",
        description: "Experience across manufacturing, healthcare, professional services, technology, remote and digital companies, retail and grocery, food service, and banking."
      },
      {
        title: "245 HR Templates",
        description: "A complete document library covering every stage of the employee lifecycle."
      },
      {
        title: "Practical, Real-World HR",
        description: "Straightforward, just tools that actually work for small businesses."
      }
    ]
  },

  // Products data
  products: [
    {
      id: 1,
      name: "Complete Hiring Toolkit",
      description: "Everything you need to hire confidently - job descriptions, interview guides, offer templates, and onboarding checklists.",
      price: 149,
      features: [
        "25+ Job Description Templates",
        "Interview Question Banks",
        "Offer Letter Templates",
        "Background Check Guidelines",
        "New Hire Onboarding Checklist"
      ],
      popular: false
    },
    {
      id: 2,
      name: "Employee Handbook Builder",
      description: "Interactive tool to create customized, state-compliant employee handbooks tailored to your business needs and industry requirements.",
      price: 500,
      features: [
        "State-Specific Compliance Requirements",
        "Industry-Tailored Policy Templates", 
        "Interactive 4-Step Builder Process",
        "Customizable Policy Selection",
        "Professional PDF Generation",
        "Legal Requirement Analysis",
        "Remote Work Policy Integration",
        "Multi-Location Support"
      ],
      popular: true
    },
    {
      id: 3,
      name: "Compliance Audit Kit",
      description: "Comprehensive audit tools to ensure your business stays compliant with federal and state regulations.",
      price: 249,
      features: [
        "Self-Audit Checklists",
        "Legal Requirement Guides",
        "Documentation Templates",
        "Risk Assessment Tools",
        "Corrective Action Plans"
      ],
      popular: false
    }
  ],

  // Membership tiers
  memberships: [
    {
      id: 1,
      name: "Basic",
      price: 29,
      period: "month",
      description: "Essential HR resources for growing businesses",
      features: [
        "Monthly updated templates & policies",
        "Industry compliance updates",
        "Email support",
        "Resource library access",
        "Basic legal requirement guides"
      ],
      popular: false,
      ctaText: "Start Basic Plan"
    },
    {
      id: 2,
      name: "Pro",
      price: 49,
      period: "month",
      description: "Advanced HR support with expert guidance",
      features: [
        "Everything in Basic",
        "Monthly live webinars",
        "Q&A sessions with HR experts",
        "Priority email support",
        "Industry-specific resources",
        "Compliance calendar"
      ],
      popular: true,
      ctaText: "Go Pro"
    },
    {
      id: 3,
      name: "Elite",
      price: 99,
      period: "month",
      description: "Premium HR partnership with personalized support",
      features: [
        "Everything in Pro",
        "Monthly 1:1 coaching calls",
        "Exclusive compliance guides",
        "Custom policy reviews",
        "Direct HR expert access",
        "Emergency support line",
        "Unlimited Handbook Builder Access ($649 value)"
      ],
      popular: false,
      ctaText: "Get Elite Access"
    }
  ],

  // Consulting services
  consultingServices: [
    {
      id: 1,
      title: "Fractional HR Business Partnering",
      description: "Ongoing HR partnership that gives your business senior level HR support without a full-time hire.",
      features: [
        "Talent retention strategies that reduce turnover",
        "Performance management systems that drive results",
        "Employee relations support for complex issues",
        "Organizational development & HR process design"
      ]
    },
    {
      id: 2,
      title: "Compliance Audits",
      description: "Deep dive HR and compliance reviews to protect your business and prevent costly violations.",
      features: [
        "Federal & state law compliance checks",
        "Policy gap analysis & required updates",
        "Risk assessment & recommendations",
        "Corrective action planning & implementation support"
      ]
    },
    {
      id: 3,
      title: "Industry-Specific HR Support",
      description: "Specialized HR guidance tailored to the realities of your industry.",
      features: [
        "Compliance guidance tailored to your industry's regulations",
        "Workforce policies built for your specific operations",
        "Support for remote, hybrid, and multi-state teams",
        "Practical HR solutions that fit how your business actually runs"
      ]
    }
  ],

  // FAQ data
  faqs: [
    {
      id: 1,
      question: "What's the difference between buying a bundle and joining a membership?",
      answer: "Bundles are one-time purchases you own forever. Memberships give ongoing access to the full library, new templates, compliance updates, and support, with the flexibility to cancel anytime."
    },
    {
      id: 2,
      question: "How do download limits work?",
      answer: "Each tier includes a set number of template downloads per month (Standard 10, Pro 15, Elite unlimited). You can browse and preview everything in your tier anytime; the limit applies only to downloading files, and it resets at the start of your billing cycle each month."
    },
    {
      id: 3,
      question: "Can I upgrade or cancel my membership anytime?",
      answer: "Yes. You can upgrade, downgrade, or cancel anytime from your account page. Changes take effect at your next billing cycle."
    },
    {
      id: 4,
      question: "Do memberships include consulting or live support?",
      answer: "Memberships include email-based HR guidance within your tier's request limit. Live meetings, investigations, and hands-on projects are available separately through DriveHR Consulting Services."
    },
    {
      id: 5,
      question: "What makes DriveHR different from other HR consultants?",
      answer: "We combine the convenience of digital products with personalized expert guidance. Our approach is practical, not overwhelming. We speak your language, not legalese."
    },
    {
      id: 6,
      question: "Do I need a membership to buy individual products?",
      answer: "No! Our digital products are available for one-time purchase. However, members get exclusive access to updated versions and additional resources."
    },
    {
      id: 7,
      question: "Can you help with industry-specific HR challenges?",
      answer: "Yes. We work with businesses across industries including manufacturing, healthcare, professional services, technology, remote and digital companies, retail and grocery, food service and restaurants, and banking and credit unions. Our solutions are tailored to your industry's realities."
    },
    {
      id: 9,
      question: "How do I access my templates after I buy?",
      answer: "For one-time bundle purchases, you get instant access. Right after checkout you can download your files directly, and we also email you a link so you can return anytime. For memberships, you log in to the HR Hub anytime to browse, preview, and download templates in your plan."
    },
    {
      id: 10,
      question: "Is my payment secure?",
      answer: "Yes. All payments are processed securely through Stripe, a trusted payment provider. DriveHR never sees or stores your full card details."
    }
  ],

  // Company stats
  stats: [
    { label: "Businesses Served", value: "500+", icon: "Building2" },
    { label: "Years Experience", value: "15+", icon: "Calendar" },
    { label: "Industries Covered", value: "12+", icon: "Target" },
    { label: "HR Templates", value: "245", icon: "FileText" }
  ],

  // Navigation items
  navigation: [
    { label: "Products", href: "#products" },
    { label: "Memberships", href: "#memberships" },
    { label: "Consulting", href: "#consulting" },
    { label: "About Us", href: "#about" },
    { label: "Contact", href: "#contact" }
  ]
};